import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { Flex } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import { formatNumbersForDisplay } from 'utils/helpers'
import { IFuelDrumFloat } from 'types/fuelOps.interface'
import {
    FUEL_DEMO_DRUM_FLOATS,
    FUEL_DRUM_FLOAT_SUMMARY_SUFFIX,
} from 'pages/FuelManagement/fuelOpsDemoData'
import {
    DrumCellEm,
    DrumCellNote,
    DrumCellStack,
    DrumCellText,
    DrumFloatValue,
    DrumSummaryLine,
} from './DrumFloatPanel.styled'

const litres = (value: number): string => `${formatNumbersForDisplay(value)} L`

const columns: IHonestTableColumn<IFuelDrumFloat>[] = [
    {
        id: 'ref',
        header: 'REF',
        cell: (item) => (
            <DrumCellStack>
                <DrumCellEm>{item.ref}</DrumCellEm>
                <DrumCellNote>{item.note}</DrumCellNote>
            </DrumCellStack>
        ),
    },
    { id: 'issuedTo', header: 'ISSUED TO', cell: (item) => <DrumCellText>{item.issuedTo}</DrumCellText> },
    {
        id: 'issued',
        header: 'ISSUED',
        align: 'right',
        cell: (item) => (
            <DrumCellStack>
                <DrumCellText>{litres(item.issuedL)}</DrumCellText>
                <DrumCellNote>{item.issuedLabel}</DrumCellNote>
            </DrumCellStack>
        ),
    },
    {
        id: 'confirmedUsed',
        header: 'CONFIRMED USED',
        align: 'right',
        cell: (item) => (
            <DrumCellStack>
                <DrumCellText>{litres(item.confirmedUsedL)}</DrumCellText>
                <DrumCellNote>{item.confirmedVia}</DrumCellNote>
            </DrumCellStack>
        ),
    },
    {
        id: 'returnedEmpty',
        header: 'RETURNED EMPTY',
        align: 'right',
        cell: (item) => <DrumCellText>{litres(item.returnedEmptyL)}</DrumCellText>,
    },
    {
        id: 'float',
        header: 'FLOAT',
        align: 'right',
        cell: (item) => <DrumFloatValue $open={item.status === 'OPEN'}>{litres(item.floatL)}</DrumFloatValue>,
    },
    {
        id: 'status',
        header: 'STATUS',
        cell: (item) => (
            <StatusPill status={item.status === 'OPEN' ? 'warning' : 'success'}>{item.status}</StatusPill>
        ),
    },
]

export const DrumFloatPanel = () => {
    const rows: IFuelDrumFloat[] = FUEL_DEMO_DRUM_FLOATS
    const openFloatL = rows
        .filter((row) => row.status === 'OPEN')
        .reduce((sum, row) => sum + row.floatL, 0)
    return (
        <Container>
            <ContainerHeader>
                <Flex center gap='0.75rem'>
                    <TitleText>Drum float</TitleText>
                    {rows.length > 0 && <DemoBadge />}
                </Flex>
            </ContainerHeader>
            <ContainerContent gap='0.75rem'>
                <HonestTable
                    columns={columns}
                    data={rows}
                    keyField='id'
                    emptyMessage='Drum issues appear here once drum custody tracking is connected.'
                />
                {openFloatL > 0 && (
                    <DrumSummaryLine>
                        {`On-site drum float: ${formatNumbersForDisplay(openFloatL)} L — ${FUEL_DRUM_FLOAT_SUMMARY_SUFFIX}`}
                    </DrumSummaryLine>
                )}
            </ContainerContent>
        </Container>
    )
}
